"use client";

import { useEffect, useState } from "react";
import { toPluralEndpoint } from "@/lib/groupUtils";
import { formatEventDate } from "@/lib/eventTime";

type MeetingEvent = {
    id: string;
    title: string;
    status: string;
    start_at: string | null;
    end_at: string | null;
    location: string | null;
    theme: string | null;
    meeting_timezone?: string | null;
};

type EventsData = {
    upcoming: MeetingEvent[];
    highlights: MeetingEvent[];
};

type Props = {
    entityType: string;
    entityId: string;
};

const API_BASE = typeof window !== "undefined"
    ? ""
    : (process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001");

const DARK = "#003d45";
const TEAL = "#007a8a";

// In the event's zone, not the reader's: a late-evening event abroad otherwise
// shows the wrong calendar day.
function formatDate(iso: string | null, timeZone?: string | null): string {
    return formatEventDate(iso, timeZone, { month: "short", day: "numeric", year: "numeric" });
}

function StatusPill({ status }: { status: string }) {
    const s = status.toLowerCase();
    const styles: Record<string, string> = {
        scheduled: "bg-teal-50 text-teal-700 border border-teal-200",
        published:  "bg-blue-50 text-blue-700 border border-blue-200",
        completed:  "bg-gray-100 text-gray-600 border border-gray-200",
    };
    const cls = styles[s] || "bg-gray-100 text-gray-500 border border-gray-200";
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full text-[0.65rem] font-bold uppercase tracking-wide ${cls}`}>
            {status}
        </span>
    );
}

function UpcomingCard({ event }: { event: MeetingEvent }) {
    return (
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-2 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-bold leading-snug" style={{ color: DARK }}>{event.title}</p>
                <StatusPill status={event.status} />
            </div>
            {event.start_at && (
                <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: TEAL }}>
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {formatDate(event.start_at, event.meeting_timezone)}
                    {event.end_at && event.end_at !== event.start_at && (
                        <span className="text-gray-400 font-normal">→ {formatDate(event.end_at, event.meeting_timezone)}</span>
                    )}
                </div>
            )}
            {event.location && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {event.location}
                </div>
            )}
            {event.theme && (
                <p className="text-xs text-gray-500 italic mt-1">{event.theme}</p>
            )}
        </div>
    );
}

function HighlightCard({ event }: { event: MeetingEvent }) {
    return (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 flex flex-col gap-1.5">
            <p className="text-sm font-semibold leading-snug text-gray-700">{event.title}</p>
            {event.start_at && (
                <p className="text-xs text-gray-400 font-medium">{formatDate(event.start_at, event.meeting_timezone)}</p>
            )}
            {event.location && (
                <p className="text-xs text-gray-400">{event.location}</p>
            )}
            <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[0.6rem] font-bold uppercase tracking-wide bg-gray-200 text-gray-500 w-max">
                Concluded
            </span>
        </div>
    );
}

function SkeletonCard() {
    return (
        <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
            <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
    );
}

export default function EventsSection({ entityType, entityId }: Props) {
    const [data, setData] = useState<EventsData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const plural = toPluralEndpoint(entityType);
        fetch(`${API_BASE}/api/v1/organizations/${plural}/${entityId}/events`, {
            cache: "no-store",
        })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((d: EventsData) => setData(d))
            .catch(() => setData({ upcoming: [], highlights: [] }))
            .finally(() => setLoading(false));
    }, [entityType, entityId]);

    // Nothing to render once loaded and both lists empty
    if (!loading && data && data.upcoming.length === 0 && data.highlights.length === 0) {
        return null;
    }

    return (
        <div style={{ backgroundColor: "#ffffff" }} className="rounded-xl p-8 mb-8 shadow-sm border border-gray-200">
            {/* Upcoming Events */}
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: DARK }}>
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                </div>
                <h3 className="text-xl font-bold" style={{ color: DARK }}>Upcoming Events</h3>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
                </div>
            ) : data && data.upcoming.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    {data.upcoming.map((e) => <UpcomingCard key={e.id} event={e} />)}
                </div>
            ) : (
                <p className="text-sm italic mb-8" style={{ color: "#666666" }}>
                    No upcoming events scheduled for this group.
                </p>
            )}

            {/* Event Highlights — only rendered if there are past events */}
            {!loading && data && data.highlights.length > 0 && (
                <>
                    <hr className="border-gray-200 mb-6" />
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: TEAL }}>
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold" style={{ color: DARK }}>Event Highlights</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {data.highlights.map((e) => <HighlightCard key={e.id} event={e} />)}
                    </div>
                </>
            )}
        </div>
    );
}
